import { useClientUser, useReturnOrderFlow } from '@/hooks';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import useAction from '@/utilities/useQuery';
import { captureException } from '@sentry/react';
import { createContext, useMemo, useState } from 'react';
import { normalizeManifestDetail } from '../Manifests/normalize';
import { useNavigate, useParams } from 'react-router';
import { useExceptionCallbacks } from '@/components/OrderDetailsPage/hooks';
import { createOrderJob } from '@/components/ShipmentForm/queries/createOrderJob';
import {
    INSERT_EXCEPTION_EVENT,
    UPDATE_EXCEPTION,
    INSERT_EXCEPTION,
} from '@/components/OrderDetailsPage/graphql/mutations';
import { RESOLVE_EXCEPTION } from '@/components/admin/AdminExceptions/graphql';
import {
    DELETE_MANIFEST,
    GET_LOAD_FEEDBACK_QUESTIONS,
    GET_MANIFEST,
    GET_ITEM,
    GET_PALLET_ITEM_IDS,
    GET_PALLET_ITEM_ORDERS,
    INSERT_EMPTY_PALLET,
    INSERT_ITEM_PALLETS_MANY,
    INSERT_PALLET,
    ROLLBACK_RECEIVE_EVENTS,
    UPDATE_ITEM_PALLETS_MANY,
    UPDATE_MANIFEST,
    UPDATE_ORDERS,
    UPDATE_PALLET,
    UPDATE_PALLETS_MANY,
    UPSERT_MANIFEST_SURVEY_RESPONSES,
} from './graphql';
import { useManifestCallbacks } from './hooks';
import { buildSurveyResponseObjects, resolveLoadFeedbackQuestions } from './Survey/utils';
import { buildPickBlockers, collectPalletIds } from './ManifestGrid/pickBlockers';
import { lockedUnwindOrders } from './ManifestGrid/utils';
import { OUTBOUND_MANIFEST_TYPES } from '@/constants/manifestStatuses';
import { printManifestItemLabels, printManifestPalletLabels } from '@/utilities/printManifestLabels';
import { UPSERT_ITEMS } from '../Manifests/graphql';
import { ITEM_READONLY_FIELDS } from '@onward-delivery/core';
import { singlePalletAtReceived } from '@/utilities/manifestPallets';
import { omit } from 'lodash';

export const Context = createContext();

// Floor work is minutes-paced, not seconds-paced — fast enough that a scan lands while the receiver
// is still at the rack, slow enough not to refetch the whole manifest constantly.
const MANIFEST_POLL_INTERVAL = 30000;

const ContextProvider = ({ children }) => {
    const { user_id } = useClientUser();
    const { manifest_id } = useParams();
    const [notification, setNotification] = useState({});
    const [editingManifest, editManifest] = useState(null);
    const [storingPallets, storePalletsInit] = useState(null);
    const [palletLog, openPalletLog] = useState(null);
    const [consolidatingPallets, consolidatePalletsInit] = useState(null);
    const [stagingPallets, stagePalletsInit] = useState(null);
    const [renamingPallet, renamePalletInit] = useState(null);
    const [editingException, editException] = useState(null);
    const [resolveExceptionParams, setResolveExceptionParams] = useState({});
    const [stagingZoneManifest, setStagingZoneManifest] = useState(null);
    // { item, order } targeted by the "Partially receive" dialog.
    const [partialReceiveItem, openPartialReceive] = useState(null);
    const [unwind, setUnwind] = useState(null);

    const navigate = useNavigate();

    const { data, loading: queryLoading } = useQuery(GET_MANIFEST, {
        variables: { manifest_id },
        skip: !manifest_id,
        fetchPolicy: 'cache-and-network',
        returnPartialData: true,
        notifyOnNetworkStatusChange: true,
        // Receiving and staging also happen on mobile, and this page has no change feed — without a
        // poll a floor screen shows scans from another device as outstanding until someone reloads.
        pollInterval: MANIFEST_POLL_INTERVAL,
        // A warehouse PC leaves this open all shift; GET_MANIFEST is heavy enough not to run it
        // against a tab nobody is looking at. Apollo resumes on the next tick after refocus.
        skipPollAttempt: () => document.visibilityState !== 'visible',
    });

    const manifest = useMemo(() => {
        if (!data?.manifest) return {};
        // GET_MANIFEST fetches each order's sibling-item list once (top-level `orders`) instead of once per manifest item to avoid a quadratic response size on large manifests.
        // Attach order sibling items from `orders` to manifest items to keep expected data shape; if `orders` isn't loaded yet, skip to avoid incomplete data.
        if ((data.manifest.items || []).length > 0 && !data.orders) return {};
        const siblingItemsByOrderId = Object.fromEntries(
            (data.orders || []).map((order) => [order.order_id, order.itemsByOrderId])
        );
        const manifestWithSiblings = {
            ...data.manifest,
            items: (data.manifest.items || []).map((mappedItem) => ({
                ...mappedItem,
                item: {
                    ...mappedItem.item,
                    order: {
                        ...mappedItem.item.order,
                        itemsByOrderId: siblingItemsByOrderId[mappedItem.item.order_id] || [],
                    },
                },
            })),
        };
        return normalizeManifestDetail(manifestWithSiblings);
    }, [data]);

    // The manifest's owner can edit it.
    const editable = !!data?.manifest?.client_id && data.manifest.client_id === user_id;

    // Outbound only — no other manifest type picks or stages. Read off the unfiltered manifest, since
    // the grid is handed a viewer-filtered order list and a pallet shared with a hidden order would
    // otherwise read as single-order.
    const outboundPalletIds = useMemo(
        () => (OUTBOUND_MANIFEST_TYPES.includes(manifest.type) ? collectPalletIds(manifest.orders) : []),
        [manifest.type, manifest.orders]
    );

    const {
        data: palletSpreadData,
        loading: palletSpreadLoading,
        error: palletSpreadError,
    } = useQuery(GET_PALLET_ITEM_IDS, {
        variables: { pallet_ids: outboundPalletIds },
        skip: outboundPalletIds.length === 0,
        fetchPolicy: 'cache-and-network',
    });

    const pickBlockers = useMemo(
        () =>
            buildPickBlockers({
                orders: manifest.orders,
                palletSpread: palletSpreadData?.items,
                spreadFailed: outboundPalletIds.length > 0 && !!palletSpreadError,
                spreadPending: outboundPalletIds.length > 0 && palletSpreadLoading && !palletSpreadData,
            }),
        [manifest.orders, outboundPalletIds.length, palletSpreadData, palletSpreadLoading, palletSpreadError]
    );

    // Shipper's Load Feedback form, falling back to the carrier's. No partner_client_id (returns,
    // cross-dock) means no questions.
    const { data: surveyQuestionsData } = useQuery(GET_LOAD_FEEDBACK_QUESTIONS, {
        variables: { client_ids: [manifest.partner_client_id, manifest.client_id].filter(Boolean) },
        skip: !manifest.partner_client_id,
    });
    const surveyQuestions = useMemo(
        () =>
            resolveLoadFeedbackQuestions({
                questions: surveyQuestionsData?.survey_questions,
                shipperId: manifest.partner_client_id,
                carrierId: manifest.client_id,
            }),
        [surveyQuestionsData, manifest.partner_client_id, manifest.client_id]
    );

    const surveyResponses = useMemo(
        () => Object.fromEntries((manifest.survey_responses || []).map((r) => [r.survey_id, r])),
        [manifest.survey_responses]
    );

    const onError = (msg) => (error) => {
        console.error(error);
        captureException(error);
        setNotification({
            severity: 'error',
            message: msg || 'Something went wrong',
        });
    };

    const [upsertSurveyResponses, { loading: surveySaveLoading }] = useMutation(UPSERT_MANIFEST_SURVEY_RESPONSES, {
        refetchQueries: [{ query: GET_MANIFEST, variables: { manifest_id } }],
        awaitRefetchQueries: true,
        onError: onError('Failed to save survey responses'),
    });

    // Resolves true once the draft is saved (or there was nothing to save), false on failure.
    const saveSurveyResponses = async (draft) => {
        if (!manifest.manifest_id) return false;
        const objects = buildSurveyResponseObjects({
            questions: surveyQuestions,
            responses: surveyResponses,
            draft,
            manifest_id: manifest.manifest_id,
        });
        if (objects.length === 0) return true;

        const { data } = await upsertSurveyResponses({ variables: { objects } });
        return !!data;
    };

    const [updateManifest, { loading: updateLoading }] = useMutation(UPDATE_MANIFEST, {
        onError: onError('Failed to update manifest'),
    });
    const [deleteManifest, { loading: deleteLoading }] = useMutation(DELETE_MANIFEST, {
        onError: onError('Failed to delete manifest'),
        onCompleted: () => {
            navigate('/manifests');
        },
    });
    const [insertPallet, { loading: insertPalletLoading }] = useMutation(INSERT_PALLET, {
        onError: onError('Failed to receive item'),
    });
    const [insertItemPallets, { loading: insertItemPalletsLoading }] = useMutation(INSERT_ITEM_PALLETS_MANY, {
        onError: onError('Failed to create pallets'),
    });
    const [updateItemPallets, { loading: updateItemPalletsLoading }] = useMutation(UPDATE_ITEM_PALLETS_MANY, {
        onError: onError('Failed to update pallets'),
        refetchQueries: [GET_MANIFEST],
        awaitRefetchQueries: true,
    });
    const [updatePallet, { loading: updatePalletLoading }] = useMutation(UPDATE_PALLET, {
        onError: onError('Failed to update pallet'),
    });
    const [updatePallets, { loading: updatePalletsLoading }] = useMutation(UPDATE_PALLETS_MANY, {
        onError: onError('Failed to update pallets'),
    });
    const [insertEmptyPallet, { loading: insertEmptyPalletLoading }] = useMutation(INSERT_EMPTY_PALLET, {
        onError: onError('Failed to add new pallet'),
    });
    // Refetch: the order's derived warehouse fields are computed server-side off these rows.
    const [rollbackReceiveEvents, { loading: rollbackLoading }] = useMutation(ROLLBACK_RECEIVE_EVENTS, {
        refetchQueries: [GET_MANIFEST],
        awaitRefetchQueries: true,
        onError: onError('Failed to roll back the received status'),
    });
    const [insertException, { loading: insertExceptionLoading }] = useMutation(INSERT_EXCEPTION, {
        update: (cache, { data: { created } }) => {
            cache.updateQuery(
                {
                    query: GET_MANIFEST,
                    variables: { manifest_id },
                },
                (data) => {
                    const clone = { ...data?.manifest };
                    clone.items = [...clone.items];

                    const exception = created.returning[0];
                    exception.mappings.forEach((mapping) => {
                        const idx = clone.items.findIndex((item) => item.item_id === mapping.item_id);
                        if (idx < 0) return;

                        clone.items.splice(idx, 1, {
                            ...clone.items[idx],
                            item: {
                                ...clone.items[idx].item,
                                exceptions: [...clone.items[idx].item.exceptions, { ...mapping, exception }],
                            },
                        });
                    });

                    // Preserve orders — GET_MANIFEST's sibling-item lists live there, not on manifest.
                    return {
                        manifest: clone,
                        orders: data?.orders,
                    };
                }
            );
        },
        onError: onError('Failed to report exception'),
    });

    const [updateException, { loading: updateExceptionLoading }] = useMutation(UPDATE_EXCEPTION, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery(
                {
                    query: GET_MANIFEST,
                    variables: { manifest_id },
                },
                (data) => {
                    const clone = { ...data?.manifest };

                    clone.items = [...clone.items];
                    clone.items = clone.items.map((item) => {
                        const clone = [...item.item.exceptions];
                        const idx = clone.findIndex(
                            (ex) => ex.exception.exception_id === updated.returning[0].exception_id
                        );

                        if (idx >= 0) {
                            const next = {
                                ...clone[idx],
                                exception: {
                                    ...clone[idx].exception,
                                    ...updated.returning[0].exception,
                                },
                            };

                            clone.splice(idx, 1, next);
                        }

                        return {
                            ...item,
                            item: {
                                ...item.item,
                                exceptions: clone,
                            },
                        };
                    });

                    // Preserve orders — GET_MANIFEST's sibling-item lists live there, not on manifest.
                    return {
                        manifest: clone,
                        orders: data?.orders,
                    };
                }
            );
        },
        onError: onError('Failed to report exception'),
    });
    const [insertExceptionEvent, { loading: insertExceptionEventLoading }] = useMutation(INSERT_EXCEPTION_EVENT, {
        refetchQueries: [GET_MANIFEST],
        awaitRefetchQueries: true,
        onError: onError('Failed to report exception'),
    });
    const [resolveException, { loading: resolveInflight }] = useMutation(RESOLVE_EXCEPTION, {
        update(cache, { data: { order, exception, items_removed } }) {
            cache.updateQuery(
                {
                    query: GET_MANIFEST,
                    variables: { manifest_id },
                },
                (data) => {
                    const clone = { ...data?.manifest };
                    const removed = Object.fromEntries(items_removed.returning.map((item) => [item.item_id, true]));
                    const [resolved] = exception.returning;

                    clone.items = [...clone.items];

                    clone.items = clone.items
                        .filter((item) => !removed[item.item_id])
                        .map((item) => {
                            const clone = [...item.item.exceptions];
                            const idx = clone.findIndex((ex) => ex.exception.exception_id === resolved.exception_id);

                            if (idx >= 0) {
                                const updated = {
                                    ...clone[idx],
                                    exception: {
                                        ...clone[idx].exception,
                                        status: resolved.status,
                                    },
                                };

                                clone.splice(idx, 1, updated);
                            }

                            return {
                                ...item,
                                item: {
                                    ...item.item,
                                    exceptions: clone,
                                },
                            };
                        });

                    // Preserve orders — GET_MANIFEST's sibling-item lists live there, not on manifest.
                    return {
                        manifest: clone,
                        orders: data?.orders,
                    };
                }
            );
        },
        onError: (err) => {
            onError('Failed to resolve exception');
        },
    });
    const [updateOrders, { loading: updateOrdersLoading }] = useMutation(UPDATE_ORDERS, {
        refetchQueries: [GET_MANIFEST],
        awaitRefetchQueries: true,
        onError: onError('Failed to update orders'),
    });

    // Partial-receive item split (reuses the create-flow UPSERT_ITEMS pattern). Awaits the refetch
    // so the grid shows the remainder row before the follow-up receive lands.
    const [upsertItemsSplit] = useMutation(UPSERT_ITEMS, {
        refetchQueries: [GET_MANIFEST],
        awaitRefetchQueries: true,
        onError: (error) => {
            console.error(error);
            captureException(error);
            setNotification({ severity: 'error', message: 'Error splitting item' });
        },
    });

    // Fetch the full item on demand so the remainder clone has every column.
    const [getFullItem] = useLazyQuery(GET_ITEM, { fetchPolicy: 'network-only' });

    // Consolidation checks pallets for items outside this manifest before deleting them.
    const [getPalletItems] = useLazyQuery(GET_PALLET_ITEM_IDS, { fetchPolicy: 'network-only' });

    const [getPalletItemOrders] = useLazyQuery(GET_PALLET_ITEM_ORDERS, { fetchPolicy: 'network-only' });

    const saveAssociatedReturnOrder = useReturnOrderFlow({
        setReturnOrderId: (oid, rid) =>
            updateOrders({ variables: { order_id: oid, update: { return_order_id: rid } } }),
    });

    const [submitOrders, { loading: submitInflight }] = useAction(createOrderJob, {
        onError: (err) => {
            onError('Failed to resolve exception');
        },
    });

    // Assign a warehouse location per item. An item's location lives on its pallet, so any item
    // without its own pallet first gets a SINGLE pallet (so it can hold an independent location);
    // items already on a pallet reuse it. Then the Store modal opens for the resulting pallets.
    const assignItemLocations = async (items = []) => {
        const seen = new Set();
        const pallets = [];
        const needPallets = [];
        for (const item of items) {
            if (item?.pallet?.pallet_id) {
                if (!seen.has(item.pallet.pallet_id)) {
                    seen.add(item.pallet.pallet_id);
                    pallets.push(item.pallet);
                }
                continue;
            }
            needPallets.push(item);
        }

        if (needPallets.length) {
            const res = await insertItemPallets({
                variables: {
                    items: needPallets.map((item) => ({
                        item_id: item.item_id,
                        pallet: { data: singlePalletAtReceived(manifest.warehouse_id) },
                    })),
                },
            });
            (res?.data?.items?.returning || []).forEach((item) => {
                if (item?.pallet) pallets.push(item.pallet);
            });
        }

        if (pallets.length) storePalletsInit(pallets);
    };

    // Opens immediately, then resolves what these pallets actually hold — a shared pallet makes the
    // unwind reach past the clicked order. Confirm stays disabled until this lands.
    const openUnwind = async ({ mode, order, pallets }) => {
        setUnwind({ mode, order, pallets, overspill: null });
        const stillOpen = (curr) => curr && curr.order?.order_id === order?.order_id;

        try {
            const { data, error } = await getPalletItemOrders({
                variables: { pallet_ids: pallets.map((pallet) => pallet.pallet_id) },
            });
            if (error) throw error;

            const ownItems = new Set((order?.manifestItems || []).map((item) => item.item_id));
            const rows = data?.items || [];

            const ordersById = new Map([[order.order_id, { ...order }]]);
            rows.forEach(({ order: coTenant }) => {
                if (coTenant?.order_id && !ordersById.has(coTenant.order_id)) ordersById.set(coTenant.order_id, coTenant);
            });

            setUnwind((curr) =>
                stillOpen(curr)
                    ? {
                          ...curr,
                          overspill: rows.filter(({ item_id }) => !ownItems.has(item_id)).length,
                          affectedOrders: [...ordersById.values()],
                          otherOrderNumbers: [...ordersById.values()]
                              .filter((o) => o.order_id !== order.order_id)
                              .map((o) => o.order_number)
                              .filter(Boolean),
                          // Any locked order anywhere in the blast radius blocks the whole unwind —
                          // un-receive flips the pallet, so it cannot spare one order on it.
                          lockedOrderNumbers: lockedUnwindOrders([...ordersById.values()], rows)
                              .map((o) => o.order_number || o.order_id)
                              .filter(Boolean),
                      }
                    : curr
            );
        } catch (err) {
            // Close rather than let the user confirm without knowing the blast radius.
            setUnwind((curr) => (stillOpen(curr) ? null : curr));
            onError('Could not check what else is on these pallets')(err);
        }
    };

    const callbacks = useManifestCallbacks(
        { manifest },
        {
            setNotification,
            onError,
            insertPallet,
            rollbackReceiveEvents,
            insertEmptyPallet,
            getPalletItems,
            updateItemPallets,
            updatePallet,
            updatePallets,
            updateManifest,
            insertException,
            updateException,
            updateOrders,
            saveAssociatedReturnOrder,
        }
    );

    const { createException } = useExceptionCallbacks(
        { order: editingException?.order || {}, exceptionParams: editingException, user_id },
        { insertExceptionEvent, insertException, updateException }
    );

    // Print straight to the OS print dialog — the grid selection IS the choice, so there's no
    // intermediate picker (the shared PrintLabelsModal still serves the order pages).
    const runPrint = async (print, noun) => {
        try {
            const printed = await print();
            if (!printed) setNotification({ severity: 'warning', message: `No ${noun} labels for that selection` });
        } catch (error) {
            captureException(error);
            setNotification({ severity: 'error', message: `Failed to print ${noun} labels` });
        }
    };

    const printItemLabels = (narrowedManifest) => runPrint(() => printManifestItemLabels(narrowedManifest), 'item');
    const printPalletLabels = (narrowedManifest) =>
        runPrint(() => printManifestPalletLabels(narrowedManifest), 'pallet');

    // Partial receive: split the targeted item into a received portion (kept on its row, then
    // received) + a not-received remainder (a new row that stays on this manifest). Reuses the
    // create-flow UPSERT_ITEMS split; the full item is fetched first so the remainder clone is complete.
    const confirmPartialReceive = async (receivedQty) => {
        const target = partialReceiveItem;
        openPartialReceive(null);
        if (!target?.item) return;

        const { item, order } = target;
        const qty = item.quantity || 1;
        const received = Math.max(1, Math.min(Number(receivedQty) || qty, qty));
        const itemWithOrder = { ...item, order: item.order || order };

        // Whole receive — nothing to split.
        if (received >= qty) {
            return callbacks.receiveItem(itemWithOrder, manifest);
        }

        const { data } = await getFullItem({ variables: { item_id: item.item_id } });
        const fullItem = data?.items_by_pk;
        if (!fullItem) {
            setNotification({ severity: 'error', message: 'Could not load item to split' });
            return;
        }

        const {
            item_id,
            quantity,
            pallet_id,
            order: _order,
            order_number,
            ...itemFields
        } = omit(fullItem, ITEM_READONLY_FIELDS);
        const receivedRow = { ...itemFields, item_id, quantity: received, pallet_id };
        const remainderRow = {
            ...itemFields,
            quantity: qty - received,
            // New row, so it can't reuse the original's line_id — (order_id, line_id) is unique.
            line_id: null,
            manifests: {
                data: { manifest_id: manifest.manifest_id },
                on_conflict: {
                    constraint: 'manifests_inbound_items_pkey',
                    update_columns: ['item_id', 'manifest_id'],
                },
            },
        };

        await upsertItemsSplit({ variables: { items: [receivedRow, remainderRow] } });
        // The split leaves a not-received remainder, so this receive can't complete the order.
        return callbacks.receiveItem(itemWithOrder, manifest, { completesOrder: false });
    };

    return (
        <Context.Provider
            value={{
                state: {
                    manifest,
                    editable,
                    notification,
                    editingManifest,
                    stagingZoneManifest,
                    storingPallets,
                    renamingPallet,
                    palletLog,
                    consolidatingPallets,
                    stagingPallets,
                    editingException,
                    resolveExceptionParams,
                    partialReceiveItem,
                    unwind,
                    surveyQuestions,
                    surveyResponses,
                    pickBlockers,
                },
                loading: {
                    resolveExceptionLoading: submitInflight || resolveInflight,
                    queryLoading,
                    surveySaveLoading,
                    updateLoading,
                    deleteLoading,
                    palletLoading:
                        rollbackLoading ||
                        insertPalletLoading ||
                        insertItemPalletsLoading ||
                        updatePalletLoading ||
                        updatePalletsLoading ||
                        updateItemPalletsLoading ||
                        insertEmptyPalletLoading,
                    exceptionLoading: updateExceptionLoading || insertExceptionLoading || insertExceptionEventLoading,
                    ordersLoading: updateOrdersLoading,
                },
                callbacks: {
                    ...callbacks,
                    insertEmptyPallet,
                    setNotification,
                    clearNotification: () => setNotification({}),
                    editManifest,
                    setStagingZoneManifest,
                    saveManifest: ({ manifest_id, ...update }) =>
                        updateManifest({ variables: { manifest_id, update } }),
                    deleteManifest: ({ manifest_id }) => deleteManifest({ variables: { manifest_id } }),
                    storePalletsInit,
                    assignItemLocations,
                    renamePalletInit,
                    openPalletLog,
                    consolidatePalletsInit,
                    stagePalletsInit,
                    editException,
                    printItemLabels,
                    printPalletLabels,
                    submitOrders,
                    setResolveExceptionParams,
                    resolveException,
                    createException,
                    openPartialReceive,
                    confirmPartialReceive,
                    openUnwind,
                    closeUnwind: () => setUnwind(null),
                    saveSurveyResponses,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};

export const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );
